import { createContext, useContext, useMemo } from "react";
import { useRouter } from "next/router";
import { isSameDay } from "date-fns";
import { formatChartLabel } from "../../DatadogAnalytics/utils";
import { getValueFormatters } from "./valueFormat";
import type { ValueFormat } from "./valueFormat";

type ChartsFormat = {
  /** True when the selected date range spans a single day (hourly x-axis). */
  isSameDayRange: boolean;
  /** Formats an x-axis timestamp, respecting isSameDayRange. */
  formatXLabel: (value: number) => string;
  /** Returns the y-axis + tooltip value formatters for a value format. */
  getValueFormatters: (
    format: ValueFormat
  ) => ReturnType<typeof getValueFormatters>;
};

const buildFormat = (isSameDayRange: boolean): ChartsFormat => ({
  isSameDayRange,
  formatXLabel: (value) => formatChartLabel({ value, isSameDayRange }),
  getValueFormatters,
});

const ChartsContext = createContext<ChartsFormat | null>(null);

export function ChartsProvider({ children }: { children: React.ReactNode }) {
  const { query } = useRouter();
  const startDate = query.start_date;
  const endDate = query.end_date;

  const value = useMemo(() => {
    const isSameDayRange = isSameDay(
      startDate ? new Date(startDate as string) : new Date(),
      endDate ? new Date(endDate as string) : new Date()
    );

    return buildFormat(isSameDayRange);
  }, [startDate, endDate]);

  return (
    <ChartsContext.Provider value={value}>{children}</ChartsContext.Provider>
  );
}

export function useChartsFormat() {
  const context = useContext(ChartsContext);
  if (!context) {
    throw new Error(
      "useChartsFormat must be used within a ChartsProvider. Wrap your component tree with <ChartsProvider>."
    );
  }
  return context;
}
